/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Sujith Dachepally
 * Created: 13/11/23
 */

import React from 'react';
import { HStack, List, ListItem, Text, VStack } from '@chakra-ui/react';
import { Assignment, AssignmentChange, AssignmentChangeHistory } from '@/wpt-lib/interfaces/Assignment';
import StateDisplay from '@/components/StateDisplay/StateDisplay';
import { SimpleAlert } from '@/components/SimpleAlert/SimpleAlert';
import { useGetRequest } from '@/utils/useStatefulRequests';
import { useAsyncEffect } from '@/utils/hooks/useAsyncEffect';
import { ListResponse } from '@/wpt-lib/interfaces/listResponse';
import { formatName } from '@/wpt-lib/utils/formatName';

export interface UserStatusHistoryListProps {
    assignment: Assignment;
    currentAssignment: Assignment;
}
const UserStatusHistoryList = (props: UserStatusHistoryListProps) => {
    const { assignment, currentAssignment } = props;

    const generateHistoryAuthorString = (historyItem: AssignmentChangeHistory): string => {
        return `${new Date(historyItem.createdAt).toLocaleDateString('pt-PT')} ${formatName(
            historyItem.changedBy.user,
        )} - assignment ${historyItem.change.toLowerCase()}: `;
    };
    const {
        error: error,
        makeRequest: makeGetRequest,
        responseData: paginatedAssignmentHistoryResp,
        loading,
    } = useGetRequest<ListResponse<AssignmentChangeHistory>>();

    useAsyncEffect(async () => {
        await makeGetRequest(
            `assignment/changeHistory/${assignment.id}?change=${JSON.stringify([
                AssignmentChange.INACTIVATED,
                AssignmentChange.ACTIVATED,
            ])}`,
            {
                params: {
                    assignment: currentAssignment.id,
                },
            },
        );
    }, [assignment, currentAssignment]);

    return (
        <StateDisplay
            data={paginatedAssignmentHistoryResp?.items ?? []}
            loading={loading}
            error={!!error}
            errorRender={() => <SimpleAlert variant="subtle">Could not load status history</SimpleAlert>}
            render={(data) => {
                return (
                    <VStack w={'full'} spacing={4} alignItems={'flex-start'}>
                        {data.length === 0 && (
                            <Text color={'gray.700'} fontStyle="italic">
                                No previous history
                            </Text>
                        )}
                        {data && (
                            <List>
                                {data?.map((historyItem) => (
                                    <ListItem key={historyItem.id}>
                                        <HStack alignItems={'flex-start'}>
                                            <Text fontWeight={'semibold'}>
                                                {generateHistoryAuthorString(historyItem)}
                                                <Text as={'span'} fontWeight={'normal'}>
                                                    {historyItem.reason}
                                                </Text>
                                            </Text>
                                        </HStack>
                                    </ListItem>
                                ))}
                            </List>
                        )}
                    </VStack>
                );
            }}
        />
    );
};

export default UserStatusHistoryList;
